import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/utils/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useTermsFieldSettings } from "@/pages/contracts/components/updateTerms/hooks";
import { useTermsVersions } from "./hooks";
import { useDiscardAmendment } from "./useDiscardAmendment";
import { TermsHistoryEntry, TIMELINE_GAP } from "./TermsHistoryEntry";
import { DiscardAmendmentDialog } from "./DiscardAmendmentDialog";
import { TermsVersionStatus } from "./types";

interface Props {
  contractId: string;
  /** Applied to the root, so a parent can place this in its own layout. */
  className?: string;
}

// The contract's saved terms versions that took effect or are due to, newest
// first. Corrections never appear: they overwrite the live terms and write no
// version, which is the distinction the epic is built on. Cancelled amendments
// are filtered out below. A contract the read API excludes — a frozen per-deal
// snapshot, a duplicate — returns an empty list, so this renders nothing.
//
// Gates on `loading` too: `useFeatureFlag` starts false, so `enabled` alone
// would render the flag-off branch first and remount.
export const TermsHistory: React.FC<Props> = ({ contractId, className }) => {
  const { t } = useTranslation();
  const { enabled, loading } = useFeatureFlag(
    FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS
  );
  const navigate = useNavigate();

  const { data, isLoading } = useTermsVersions(contractId, {
    enabled: !loading && enabled
  });
  // Same query key as UpdateTermsAction's own read, so the two share a request.
  const { data: fieldSettings } = useTermsFieldSettings(contractId, {
    enabled: !loading && enabled
  });
  const discard = useDiscardAmendment(contractId);

  if (loading || !enabled) return null;

  // `className` carries the caller's grid placement, so it has to reach this
  // branch too — the tab renders this as a bare grid item, and a spinner that
  // dropped the placement would land in whatever cell auto-placement happened
  // to pick. The two `return null` branches render no cell at all, so they
  // need none.
  if (isLoading) {
    return (
      <div className={cn("flex justify-center py-6", className)}>
        <Spinner size="md" />
      </div>
    );
  }

  // Cancelled versions are left out entirely: terms that never took effect are
  // not part of the contract's history, and rendering them put a cancellation
  // reason and two separate timestamps on the rail for terms nobody ever had.
  // The version page still renders one on a direct URL, so a link saved before
  // the cancellation resolves rather than 404s.
  //
  // The endpoint serves newest-first, but the rail's first/last ends and the
  // "newest first" requirement both depend on it, so the order is asserted here
  // rather than assumed of a second caller or a later backend change. Filtering
  // precedes the sort so those two ends land on entries that actually render.
  const versions = [...(data?.versions ?? [])]
    .filter((version) => version.status !== TermsVersionStatus.WITHDRAWN)
    .sort((left, right) => right.versionIndex - left.versionIndex);
  if (versions.length === 0) return null;

  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      data-test="terms-history"
    >
      <Text variant="smSubtitle">{t("contracts.termsHistory.title")}</Text>
      <div className="flex flex-col" style={{ gap: TIMELINE_GAP }}>
        {versions.map((version, index) => (
          <TermsHistoryEntry
            key={version.versionId}
            contractId={contractId}
            version={version}
            isFirst={index === 0}
            isLast={index === versions.length - 1}
            onViewTerms={(selected) =>
              navigate(
                `/contracts/${contractId}/terms/versions/${selected.versionId}`
              )
            }
            onDiscard={discard.request}
            canDiscard={fieldSettings?.canUpdateTerms ?? false}
          />
        ))}
      </div>
      {/* A refetch does not close this — `target` is a snapshot taken at click
          time. Confirming against a version that stopped being pending is what
          the server refuses, and that path closes the dialog and re-reads. */}
      {discard.target ? (
        <DiscardAmendmentDialog
          isOpen
          isDiscarding={discard.isDiscarding}
          onKeep={discard.keep}
          onConfirm={discard.confirm}
        />
      ) : null}
    </div>
  );
};
